import React, { useContext, useEffect, useMemo } from 'react'
import useBrand from '@engine/hooks/useBrand'
import Brands from '@brands/index'
import { CardInfo, Input as KitInput } from '@telus/koodo-ui-kit'
import { PHONE_NUMBER_STATE, updatePhoneNumberState } from '@apps/redux/features/PhoneNumberSlice'
import PhoneNumberChangeHandlers, {
  PHONE_NUMBER_EVENTS
} from '@apps/utils/ChangeHandlers/PhoneNumberChangeHandlers'
import { useDispatch, useSelector } from 'react-redux'
import Address from '@apps/components/Address'
import BodySmall from '@apps/components/Typography/BodySmall'
import BodySmallBold from '@apps/components/Typography/BodySmallBold'
import Sites from '@sites/index'
import { ApplicationContext, APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import { getById, getBySlug } from '@apps/utils/contentful'
import MarkdownRenderer from '@/apps/components/MarkdownRenderer'

const MAX_LENGTH_ALTERNATE_PHONE_NUMBER = 12
const FIRSTNAME_MAX_LENGTH = 11
const LASTNAME_MAX_LENGTH = 18

const TransferLandline = ({ landlineRefs, landlineHandlers, pushClickAnalytics }) => {
  const dispatch = useDispatch()
  const phoneNumberState = useSelector((state) => state.phoneNumberPage)
  const {
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.ALTERNATE_CONTACT_NUMBER]: alternateContactNumber,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.ALTERNATE_CONTACT_NUMBER_ERROR]: alternatePhoneError,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.FIRST_NAME]: firstName,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.LAST_NAME]: lastName,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.FIRST_NAME_ERROR]: firstNameError,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.LAST_NAME_ERROR]: lastNameError
  } = phoneNumberState

  const {
    content: { pageContent },
    user: { selectedLanguage },
    userType
  } = useSelector((state) => state.common)

  const isDealer = userType === Sites.dealer
  const pagePrefix = isDealer ? 'Dealer' : 'Consumer'
  const textContent = useMemo(() => {
    if (!pageContent) return {}
    const phoneNumberPageText = getById(pageContent, `phoneNumberPage${pagePrefix}`)[
      selectedLanguage
    ]
    const { shortTextFields, longTextFields } = phoneNumberPageText || {}
    return {
      firstNameLabel: getBySlug(shortTextFields, 'firstNameLabel')?.value,
      lastNameLabel: getBySlug(shortTextFields, 'lastNameLabel')?.value,
      firstNameRequired: getBySlug(shortTextFields, 'firstNameRequired')?.value,
      lastNameRequired: getBySlug(shortTextFields, 'lastNameRequired')?.value,
      alternateContactRequired: getBySlug(shortTextFields, 'alternateContactRequired')?.value,
      alternateContactInvalid: getBySlug(shortTextFields, 'alternateContactInvalid')?.value,
      important: getBySlug(shortTextFields, 'important')?.value,
      alternateContactTitle: getBySlug(shortTextFields, 'alternateContactTitle')?.value,
      alternateInfoKoodo: getBySlug(shortTextFields, 'alternateInfoKoodo')?.value,
      alternateInfoPublic: getBySlug(shortTextFields, 'alternateInfoPublic')?.value,
      transferLandlineConfirm: getBySlug(shortTextFields, 'transferLandlineConfirm')?.value,
      transferNumberTitle: getBySlug(shortTextFields, 'transferNumberTitle')?.value,
      transferNumberContent: getBySlug(longTextFields, 'transferNumberContent')?.value,
    }
  }, [pageContent, selectedLanguage])

  const {
    firstNameLabel,
    lastNameLabel,
    firstNameRequired,
    lastNameRequired,
    alternateContactRequired,
    alternateContactInvalid,
    important,
    alternateContactTitle,
    alternateInfoKoodo,
    alternateInfoPublic,
    transferLandlineConfirm
  } = textContent

  const { [APPLICATION_STATE.CURRENT_LANGUAGE]: currentLanguage } = useContext(ApplicationContext)
  const brand = useBrand()
  const { alternateLandlineRef } = landlineRefs

  const {
    handleFirstName,
    handleLastName,
    handleAltPhoneLandline: handleAltPhone
  } = landlineHandlers

  const updatePhoneNumberStore = (payload) => {
    dispatch(updatePhoneNumberState(payload))
  }

  useEffect(() => {
    if (phoneNumberState.firstNameLandlineError) {
      handleFirstName(firstName)
    }
    if (phoneNumberState.lastNameLandlineError) {
      handleLastName(lastName)
    }
    if (phoneNumberState.alternateContactNumberError) {
      handleAltPhone(alternateContactNumber)
    }
  }, [currentLanguage])

  return (
    <>
      <div className="space-y-8">
        <div className="flex flex-col items-stretch justify-center md:items-start lg:flex-row lg:items-start">
          <div className="relative w-full lg:flex-1 lg:mr-6">
            <KitInput
              type="text"
              id="firstName"
              label={firstNameLabel}
              name="firstName"
              value={firstName}
              onBlur={() => updatePhoneNumberStore({
                [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.FIRST_NAME_ERROR]: firstName ? null : firstNameRequired
              })}
              onChange={(value) => {
                PhoneNumberChangeHandlers[PHONE_NUMBER_EVENTS.INPUT].onChangeName(
                  { target: { value } },
                  PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.FIRST_NAME,
                  updatePhoneNumberStore
                )
                if (firstNameError) {
                  updatePhoneNumberStore({
                    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.FIRST_NAME_ERROR]: null
                  })
                }
              }}
              feedback={firstNameError}
              validation={firstNameError ? 'error' : undefined}
              maxLength={FIRSTNAME_MAX_LENGTH}
              data-testid="phoneNumber-firstName"
            />
          </div>
          <div className="relative w-full lg:flex-1">
            <KitInput
              type="text"
              id="lastName"
              label={lastNameLabel}
              name="lastName"
              value={lastName}
              onBlur={() => updatePhoneNumberStore({
                [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.LAST_NAME_ERROR]: lastName ? null : lastNameRequired
              })}
              onChange={(value) => {
                PhoneNumberChangeHandlers[PHONE_NUMBER_EVENTS.INPUT].onChangeName(
                  { target: { value } },
                  PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.LAST_NAME,
                  updatePhoneNumberStore
                )
                if (lastNameError) {
                  updatePhoneNumberStore({
                    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELINE.LAST_NAME_ERROR]: null
                  })
                }
              }}
              feedback={lastNameError}
              validation={lastNameError ? 'error' : undefined}
              maxLength={LASTNAME_MAX_LENGTH}
              data-testid="phoneNumber-lastName"
            />
          </div>
        </div>
        <Address landlineRefs={landlineRefs} landlineHandlers={landlineHandlers} />
        {transferLandlineConfirm && (
          <div>
            <BodySmallBold className="inline">{important}</BodySmallBold>
            <BodySmall>{` ${transferLandlineConfirm}`}</BodySmall>
          </div>
        )}
        <div ref={alternateLandlineRef} className="w-full">
          <KitInput
            type="text"
            id="alternateContactNumber"
            name="Alternate phone number"
            label={alternateContactTitle}
            feedback={alternatePhoneError}
            validation={alternatePhoneError ? 'error' : undefined}
            maxLength={MAX_LENGTH_ALTERNATE_PHONE_NUMBER}
            value={alternateContactNumber}
            hint={`${important} ${brand === Brands.koodo ? alternateInfoKoodo : alternateInfoPublic}`}
            tokens={{ hintFontSize: 12 }}
            onBlur={() => {
              let error = null
              if (!alternateContactNumber) {
                error = alternateContactRequired
              } else if (alternateContactNumber.length !== MAX_LENGTH_ALTERNATE_PHONE_NUMBER) {
                error = alternateContactInvalid
              }
              updatePhoneNumberStore({
                [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.ALTERNATE_CONTACT_NUMBER_ERROR]: error
              })
            }}
            onChange={(value) => {
              PhoneNumberChangeHandlers[PHONE_NUMBER_EVENTS.ALTERNATE_CONTACT_NUMBER].onChange(
                { target: { value } },
                updatePhoneNumberStore
              )
              if (alternatePhoneError) {
                updatePhoneNumberStore({
                  [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.ALTERNATE_CONTACT_NUMBER_ERROR]: null
                })
              }
            }}
            data-testid="phoneNumber-alternateContact"
          />
        </div>
      </div>
      <CardInfo heading={textContent?.transferNumberTitle}>
        <MarkdownRenderer markdown={textContent?.transferNumberContent} paragraphClassForIndex={() => 'text-copy text-sm font-body leading-6'} linkPrimaryClass={false} />

      </CardInfo>

    </>
  )
}

export default TransferLandline
